import { FC, useRef } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { getInputStyle, styles } from "./stylesOtpInput";

interface OtpInputProps {
  length: number;
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  inputStyle?: React.CSSProperties;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

const OtpInput: FC<OtpInputProps> = ({
  length,
  value,
  onChange,
  label,
  error,
  helperText,
  inputStyle,
  onPaste,
}) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleKeyDown = (e: React.KeyboardEvent<any>, idx: number) => {
    const inputEl = inputsRef.current[idx];
    if (!inputEl) return;

    const newValue = [...value];
    const cursorPos = inputEl.selectionStart ?? 0;

    switch (e.key) {
      case "Backspace":
        e.preventDefault();
        if (cursorPos > 0) {
          const delIdx = idx;
          if (cursorPos === 1) {
            newValue[delIdx] = "";
          }
          for (let i = delIdx + 1; i < length; i++) {
            newValue[i - 1] = newValue[i];
          }
          newValue[length - 1] = "";
          onChange(newValue);

          inputsRef.current[delIdx]?.focus();
          inputsRef.current[delIdx]?.setSelectionRange(0, 0);
        } else if (idx > 0) {
          inputsRef.current[idx - 1]?.focus();
          inputsRef.current[idx - 1]?.setSelectionRange(1, 1);
        }
        break;

      case "Delete":
        e.preventDefault();
        if (cursorPos === 0) {
          for (let i = idx; i < length - 1; i++) {
            newValue[i] = newValue[i + 1];
          }
          newValue[length - 1] = "";
        } else if (cursorPos === 1) {
          if (idx + 1 < length) {
            for (let i = idx + 1; i < length - 1; i++) {
              newValue[i] = newValue[i + 1];
            }
            newValue[length - 1] = "";
          }
        }
        onChange(newValue);
        break;

      case "ArrowLeft":
        e.preventDefault();
        if (idx > 0) inputsRef.current[idx - 1]?.focus();
        break;

      case "ArrowRight":
        e.preventDefault();
        if (idx < length - 1) inputsRef.current[idx + 1]?.focus();
        break;

      default:
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<any>, idx: number) => {
    const inputEl = inputsRef.current[idx];
    if (!inputEl) return;

    const rawValue = e.target.value;
    if (!rawValue) return;

    const newValue = [...value];

    for (let i = 0; i < rawValue.length && idx + i < length; i++) {
      newValue[idx + i] = rawValue[i];
    }

    onChange(newValue);

    const nextIdx = Math.min(idx + rawValue.length, length - 1);
    inputsRef.current[nextIdx]?.focus();
    inputsRef.current[nextIdx]?.setSelectionRange(1, 1);
  };

  const handleInternalPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (onPaste) {
      onPaste(e);
      return;
    }

    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, length).split("");

    const newValue = [...value];
    paste.forEach((char, i) => {
      if (i < length) newValue[i] = char;
    });

    onChange(newValue);
    inputsRef.current[paste.length - 1]?.focus();
  };

  return (
    <Box sx={styles.root}>
      {label && <Typography sx={styles.label}>{label}</Typography>}

      <Box sx={styles.inputsWrapper}>
        {Array.from({ length }).map((_, idx) => (
          <TextField
            key={idx}
            variant="standard"
            inputRef={(el) => (inputsRef.current[idx] = el)}
            value={value[idx] || ""}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={handleInternalPaste}
            sx={getInputStyle(!!value[idx], error, inputStyle)}
            inputProps={{
              maxLength: 1,
            }}
          />
        ))}
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          color={error ? "error" : "textSecondary"}
          sx={styles.helperText}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default OtpInput;
